import React, { useEffect, useState } from 'react';
import { X, Send, Package, AlertCircle } from 'lucide-react';
import { shipmentService } from '../../api/shipmentService';
import SenditDistrictPicker from '../Common/SenditDistrictPicker';

/**
 * Create-a-Colis modal.
 *
 * Pre-fills every field from the order (via GET /api/shipments/preview),
 * lets the admin edit any of them, and submits via POST /api/shipments.
 *
 * The city field uses Sendit's /districts endpoint with debounced search +
 * autocomplete so the admin picks a city Sendit actually recognizes.
 */
export default function SenditColisModal({ orderId, onClose, onCreated }) {
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [form, setForm] = useState({
    name: '', phone: '', address: '', city: '',
    district_id: '', district_price: null,
    pickup_district_id: '', pickup_district_name: '', pickup_district_price: null,
    amount: '', weight: '1', reference: '', comment: '',
    allow_open: false,
  });
  const [error, setError] = useState('');
  const [fieldErrors, setFieldErrors] = useState({});

  // Initial load — fetch the pre-filled payload
  useEffect(() => {
    let alive = true;
    setLoading(true);
    shipmentService
      .preview(orderId)
      .then((res) => {
        if (!alive) return;
        const p = res.payload || {};
        setForm({
          name: p.name || '',
          phone: p.phone || '',
          address: p.address || '',
          // Pre-fill the delivery city + Sendit district chosen at checkout so the
          // admin doesn't have to re-search it. Editable if they want to change it.
          city: p.delivery_district_name || p.city || '',
          district_id: p.delivery_district_id ? String(p.delivery_district_id) : '',
          district_price: p.delivery_district_price ?? null,
          // Pre-fill pickup district from settings (sent through the preview payload)
          pickup_district_id: p.pickup_district_id ? String(p.pickup_district_id) : '',
          pickup_district_name: p.pickup_district_name || '',
          pickup_district_price: p.pickup_district_price ?? null,
          amount: p.amount?.toString() || '',
          weight: (p.weight ?? 1).toString(),
          reference: p.reference || '',
          comment: p.comment || '',
          allow_open: !!p.allow_open,
        });
      })
      .catch((e) => {
        setError(e.response?.data?.message || 'Échec du chargement des données pré-remplies.');
      })
      .finally(() => alive && setLoading(false));
    return () => { alive = false; };
  }, [orderId]);

  const setField = (k, v) => {
    setForm((f) => ({ ...f, [k]: v }));
    setFieldErrors((e) => ({ ...e, [k]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.name?.trim()) errs.name = 'Nom requis';
    if (!form.phone?.trim()) errs.phone = 'Téléphone requis';
    if (!form.address?.trim()) errs.address = 'Adresse requise';
    if (!form.district_id) errs.city = 'Sélectionnez une ville Sendit dans la liste';
    if (!form.pickup_district_id) errs.pickup_district_id = 'Sélectionnez votre ville de ramassage';
    if (form.amount === '' || isNaN(parseFloat(form.amount))) errs.amount = 'Montant invalide';
    if (form.weight === '' || isNaN(parseFloat(form.weight))) errs.weight = 'Poids invalide';
    setFieldErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');
    if (!validate()) return;
    setSubmitting(true);
    try {
      const overrides = {
        name: form.name.trim(),
        phone: form.phone.trim(),
        address: form.address.trim(),
        city: form.city.trim(),
        district_id: form.district_id || undefined,
        pickup_district_id: form.pickup_district_id || undefined,
        amount: parseFloat(form.amount),
        weight: parseFloat(form.weight),
        reference: form.reference.trim() || undefined,
        comment: form.comment.trim() || undefined,
        allow_open: !!form.allow_open,
      };
      const resp = await shipmentService.create(orderId, overrides);
      onCreated?.(resp.shipment);
      onClose?.();
    } catch (e) {
      const data = e.response?.data;
      setError(data?.message || 'Échec de la création de la livraison.');
      if (data?.errors) {
        // Laravel validation errors come back nested as overrides.*
        const flat = {};
        Object.entries(data.errors).forEach(([k, v]) => {
          const key = k.replace(/^overrides\./, '');
          flat[key] = Array.isArray(v) ? v[0] : v;
        });
        setFieldErrors(flat);
      }
    } finally {
      setSubmitting(false);
    }
  };

  const labelStyle = { fontSize: 12, fontWeight: 600, color: '#444', marginBottom: 4, display: 'block' };
  const inputStyle = (hasError) => ({
    width: '100%', padding: '8px 12px', borderRadius: 6,
    border: '1px solid ' + (hasError ? '#dc3545' : '#ced4da'),
    fontSize: 14, fontFamily: 'inherit',
  });
  const errorStyle = { color: '#dc3545', fontSize: 11, marginTop: 2 };

  return (
    <div style={{
      position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.5)',
      display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 1100,
      padding: 16,
    }} onMouseDown={(e) => { if (e.target === e.currentTarget) onClose?.(); }}>
      <div style={{
        background: '#fff', borderRadius: 10, width: '100%', maxWidth: 720,
        maxHeight: '92vh', overflow: 'hidden', display: 'flex', flexDirection: 'column',
      }}>
        {/* Header */}
        <div style={{
          padding: '16px 20px', borderBottom: '1px solid #e9ecef',
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
            <Package size={20} />
            <h3 style={{ margin: 0, fontSize: 17 }}>Créer un colis Sendit</h3>
          </div>
          <button
            type="button" onClick={onClose}
            style={{ background: 'none', border: 'none', cursor: 'pointer', padding: 4 }}
            aria-label="Fermer"
          >
            <X size={20} />
          </button>
        </div>

        {/* Body */}
        <form onSubmit={handleSubmit} style={{ flex: 1, overflowY: 'auto', padding: 20 }}>
          {loading ? (
            <div style={{ textAlign: 'center', padding: 40, color: '#666' }}>Chargement des données…</div>
          ) : (
            <>
              {error && (
                <div style={{
                  marginBottom: 16, padding: 12, background: '#f8d7da', color: '#721c24',
                  borderRadius: 6, display: 'flex', gap: 8, alignItems: 'flex-start', fontSize: 13,
                }}>
                  <AlertCircle size={16} style={{ flexShrink: 0, marginTop: 1 }} />
                  <span>{error}</span>
                </div>
              )}

              <p style={{ fontSize: 12, color: '#666', margin: '0 0 16px' }}>
                Les champs sont pré-remplis à partir de la commande. Modifiez-les si nécessaire avant d'envoyer chez Sendit.
              </p>

              {/* Recipient */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Nom du destinataire *</label>
                  <input
                    type="text" value={form.name} maxLength={150}
                    onChange={(e) => setField('name', e.target.value)}
                    style={inputStyle(fieldErrors.name)}
                  />
                  {fieldErrors.name && <div style={errorStyle}>{fieldErrors.name}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Téléphone *</label>
                  <input
                    type="text" value={form.phone} maxLength={30}
                    onChange={(e) => setField('phone', e.target.value)}
                    style={inputStyle(fieldErrors.phone)}
                    placeholder="06XXXXXXXX"
                  />
                  {fieldErrors.phone && <div style={errorStyle}>{fieldErrors.phone}</div>}
                </div>
              </div>

              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Adresse *</label>
                <input
                  type="text" value={form.address} maxLength={500}
                  onChange={(e) => setField('address', e.target.value)}
                  style={inputStyle(fieldErrors.address)}
                />
                {fieldErrors.address && <div style={errorStyle}>{fieldErrors.address}</div>}
              </div>

              {/* Pickup district (sender side) */}
              <div style={{ marginBottom: 12, padding: 10, background: '#f8f9fa', borderRadius: 6, border: '1px solid #e9ecef' }}>
                <label style={labelStyle}>Point de ramassage (votre ville) *</label>
                <SenditDistrictPicker
                  value={form.pickup_district_id}
                  valueLabel={form.pickup_district_name}
                  valuePrice={form.pickup_district_price}
                  placeholder="Ville d'où Sendit récupère le colis…"
                  onChange={({ id, name, price }) => {
                    setForm((f) => ({
                      ...f,
                      pickup_district_id: id || '',
                      pickup_district_name: name || '',
                      pickup_district_price: price ?? null,
                    }));
                    setFieldErrors((e) => ({ ...e, pickup_district_id: undefined }));
                  }}
                />
                {fieldErrors.pickup_district_id && (
                  <div style={errorStyle}>{fieldErrors.pickup_district_id}</div>
                )}
                <p style={{ fontSize: 11, color: '#666', margin: '4px 0 0' }}>
                  Pré-rempli depuis Paramètres société → Livraison Sendit. Modifiable pour ce colis si besoin.
                </p>
              </div>

              {/* City (Sendit district) + Reference */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Ville de livraison (Sendit) *</label>
                  <SenditDistrictPicker
                    value={form.district_id}
                    valueLabel={form.city}
                    valuePrice={form.district_price}
                    placeholder="Rechercher une ville…"
                    allowFreeText
                    freeText={form.city}
                    onFreeTextChange={(v) => setField('city', v)}
                    onChange={({ id, name, price }) => {
                      setForm((f) => ({
                        ...f,
                        district_id: id || '',
                        city: name || f.city,
                        district_price: price ?? null,
                      }));
                      setFieldErrors((e) => ({ ...e, city: undefined }));
                    }}
                  />
                  {form.district_price !== null && form.district_price !== undefined && (
                    <div style={{ fontSize: 11, color: '#0a3a82', marginTop: 4 }}>
                      Tarif de livraison Sendit : <strong>{parseFloat(form.district_price).toFixed(2)} DH</strong>
                    </div>
                  )}
                  {fieldErrors.city && <div style={errorStyle}>{fieldErrors.city}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Référence (n° de commande)</label>
                  <input
                    type="text" value={form.reference} maxLength={80}
                    onChange={(e) => setField('reference', e.target.value)}
                    style={inputStyle(fieldErrors.reference)}
                  />
                  {fieldErrors.reference && <div style={errorStyle}>{fieldErrors.reference}</div>}
                </div>
              </div>

              {/* Amount + Weight */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 12 }}>
                <div>
                  <label style={labelStyle}>Montant COD (DH) *</label>
                  <input
                    type="number" step="0.01" min="0" value={form.amount}
                    onChange={(e) => setField('amount', e.target.value)}
                    style={inputStyle(fieldErrors.amount)}
                  />
                  {fieldErrors.amount && <div style={errorStyle}>{fieldErrors.amount}</div>}
                </div>
                <div>
                  <label style={labelStyle}>Poids (kg) *</label>
                  <input
                    type="number" step="0.1" min="0.1" value={form.weight}
                    onChange={(e) => setField('weight', e.target.value)}
                    style={inputStyle(fieldErrors.weight)}
                  />
                  {fieldErrors.weight && <div style={errorStyle}>{fieldErrors.weight}</div>}
                </div>
              </div>

              {/* Comment */}
              <div style={{ marginBottom: 12 }}>
                <label style={labelStyle}>Description / Articles</label>
                <textarea
                  rows="2"
                  value={form.comment}
                  maxLength={1000}
                  onChange={(e) => setField('comment', e.target.value)}
                  style={{ ...inputStyle(false), resize: 'vertical' }}
                />
              </div>

              {/* Allow open */}
              <label style={{ display: 'flex', gap: 8, alignItems: 'center', fontSize: 13, color: '#444' }}>
                <input
                  type="checkbox"
                  checked={!!form.allow_open}
                  onChange={(e) => setField('allow_open', e.target.checked)}
                />
                Autoriser le client à ouvrir le colis avant paiement
              </label>
            </>
          )}
        </form>

        {/* Footer */}
        <div style={{
          padding: '14px 20px', borderTop: '1px solid #e9ecef',
          display: 'flex', justifyContent: 'flex-end', gap: 10,
        }}>
          <button
            type="button" onClick={onClose}
            disabled={submitting}
            className="btn btn-secondary"
          >
            Annuler
          </button>
          <button
            type="button" onClick={handleSubmit}
            disabled={submitting || loading}
            className="btn btn-primary"
          >
            <Send size={16} />
            {submitting ? 'Envoi…' : 'Créer le colis chez Sendit'}
          </button>
        </div>
      </div>
    </div>
  );
}
